import { CircularProgress } from "@mui/material";
import { profileBussiness } from "api/bussiness/auth/profile-bussiness";
import { observer } from "mobx-react-lite";
import { ReactNode, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authBusiness } from "../../api/bussiness/auth/auth-business";
import { useStores } from "../../mobx-stores/_root-store";

type AuthProtectProps = {
  children: ReactNode;
};

export const AuthProtect = observer(({ children }: AuthProtectProps) => {
  const {
    authentication: { isAuthenticated, setProp },
  } = useStores();
  const isProcessed = useRef(false);
  const navigate = useNavigate();

  const getProfile = useCallback(async () => {
    const profile = await profileBussiness.getProfile();
    setProp("profile", profile);
  }, [setProp]);

  const getGrantedPermissions = useCallback(async () => {
    const permissions = await profileBussiness.getGrantedPermissions();
    setProp("grantedPermissions", permissions);
  }, [setProp]);

  useEffect(() => {
    async function processOAuthResponse() {
      if (isProcessed.current) {
        return;
      }

      isProcessed.current = true;

      try {
        if (await authBusiness.isAuthenticated()) {
          await Promise.all([getGrantedPermissions(), getProfile()]);
          setProp("isAuthenticated", true);
        } else if (authBusiness.isInactive()) {
          await authBusiness.logoutAndReauth();
        } else {
          await authBusiness.sendOAuthRequest();
        }
      } catch (error) {
        console.error("Error processing OAuth callback:", error);
      }
    }

    processOAuthResponse();
  }, [getGrantedPermissions, getProfile, isAuthenticated, navigate, setProp]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const updateLastActivity = () => authBusiness.updateLastActivity();

    updateLastActivity();

    const interval = window.setInterval(updateLastActivity, 60_000);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        updateLastActivity();
      }
    };
    const onFocus = updateLastActivity;

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isAuthenticated]);

  return (
    <>
      {isAuthenticated ? (
        children
      ) : (
        <CircularProgress
          size={"small"}
          sx={{ position: "absolute", top: "50%", left: "50%" }}
        />
      )}
    </>
  );
});
